import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { useHistory } from "react-router-dom";

import Images from "../../assets";
import { getRouteUrl } from "../../helpers/routing/getRouteUrl";

import CartHeader from "../../components/general/cart/cartHeader";
import CartItemRow from "../../components/general/cart/cartItemRow";
import CartSummarySection from "../../components/general/cart/cartSummarySection";
import AddMoreSection from "../../components/general/cart/addMoreSection";
import {ROUTE_CHECKOUT, ROUTE_PRODUCT_LIST} from "../../routers/routes";
import { useTranslation } from "react-i18next";
import { PRODUCT_INFO } from "../../locales/components/namespaces";
import useCartData from "../../hooks/cart/useCartData";
import useCartAction from "../../hooks/cart/useCartAction";
import {enqueueSnackbar} from "notistack";

const TOKENS = {
  containerW: "93vw",
  containerMax: 1720,
};

const CONTAINER_W = {
  width: TOKENS.containerW,
  maxWidth: TOKENS.containerMax,
  mx: "auto",
};

const ADD_MORE_ITEMS = [
  {
    id: "6486",
    sku: "ΚΩΔΙΚΟΣ: 152420",
    name: "Θερμός Hario ανοξείδωτος",
    image: Images.product3,
    price: 19.9,
  },
  {
    id: "6486",
    sku: "ΚΩΔΙΚΟΣ: 152420",
    name: "Ζυγαριά barista compact",
    image: Images.product3,
    price: 24.5,
  },
];

export default function Cart() {
  const history = useHistory();
  const { t } = useTranslation(PRODUCT_INFO);
  const [coupon, setCoupon] = useState(null);
  const {
    cartItems,
    count,
    vat,
    sum,
      total,
    loading,
    message,
    shouldRefreshCart,
  } = useCartData();

  const {
    getCart,
    editCart,
    ackCartRefresh,
    setCartMessage
  } = useCartAction();

  useEffect(() => {
    getCart();
  }, [getCart]);


  useEffect(() => {
    if (!message) return;

    const text =
        typeof message.get === "function" ? message.get("text") : message?.text;
    const variant =
        typeof message.get === "function"
            ? message.get("variant")
            : message?.variant || "error";

    if (!text) return;

    enqueueSnackbar(t("Failed to update cart!"), {variant:"error",});

    setCartMessage(null);
  }, [message, setCartMessage, t]);

  useEffect(() => {
    if (!shouldRefreshCart) return;

    getCart();
    ackCartRefresh();
  }, [shouldRefreshCart, getCart, ackCartRefresh]);

  const handleIncreaseQty = (item) => {
    editCart({
      id: item.id,
      quantity: Number(item.quantity) + 1,
      suppressNotification: true,
    });
  };

  const handleDecreaseQty = (item) => {
    const nextQty = Math.max(1, Number(item.quantity) - 1);

    editCart({
      id: item.id,
      quantity: nextQty,
      suppressNotification: true,
    });
  };

  const handleRemoveItem = (item) => {
    editCart({
      id: item.id,
      quantity: 0,
      suppressNotification: true,
    });
  };

  const handleAddMoreItem = (id) => {
    const addItem = ADD_MORE_ITEMS.find((x) => x.id === id);
    if (!addItem) return;

    editCart({
      id: addItem.id,
      quantity: 1,
      suppressNotification: true,
      product: {
        id: String(addItem.id),
        title: addItem.name,
        image: addItem.image || "",
        price: Number(addItem.price) || 0,
        quantity: 1,
        code: addItem.sku || "",
      },
    });
  };

  const handleContinueShopping = () => {
    history.push(getRouteUrl(ROUTE_PRODUCT_LIST));
  };

  const handleApplyCoupon = (code) => {
    const normalized = code.trim().toUpperCase();

    let percent = 0;
    if (normalized === "BREW10") percent = 0.1;
    else if (normalized === "BREW20") percent = 0.2;

    if (!percent) {
      alert("Μη έγκυρος κωδικός κουπονιού.");
      return;
    }

    setCoupon({ code: normalized, percent });
  };

  const handleRemoveCoupon = () => {
    setCoupon(null);
  };

  const handleCheckout = () => {
   history.push(getRouteUrl(ROUTE_CHECKOUT))
  };

  const discountAmount = coupon ? total * coupon.percent : 0;
  const totalAfterDiscount = total - discountAmount;
  const netValue = totalAfterDiscount - vat;

  if (loading && cartItems.length === 0) {
    return (
        <Box sx={{ py: 4 }}>
          <Box sx={CONTAINER_W}>
            <CircularProgress />
          </Box>
        </Box>
    );
  }

  if (message?.variant === "error" && cartItems.length === 0) {
    return (
        <Box sx={{ py: 4 }}>
          <Box sx={CONTAINER_W}>
            <Alert severity="error">
              {message.text || t("Something went wrong")}
            </Alert>
          </Box>
        </Box>
    );
  }

  return (
      <Box sx={{ py: 4 }}>
        <Box sx={CONTAINER_W}>
          <Stack spacing={3}>
            <CartHeader totalItems={count || cartItems.length} />

            <Stack spacing={1.25}>
              {cartItems.length === 0 ? (
                  <Box
                      sx={{
                        bgcolor: "#F5F5F5",
                        borderRadius: 2,
                        p: 3,
                      }}
                  >
                    <Typography sx={{ fontWeight: 600, color: "#555" }}>
                      {t("Your cart is empty")}
                    </Typography>
                  </Box>
              ) : (
                  cartItems.map((item) => (
                      <CartItemRow
                          key={item.id}
                          item={{
                            ...item,
                            sku: item.code ? `ΚΩΔΙΚΟΣ: ${item.code}` : "",
                            name: item.title,
                            unitPrice: item.price,
                            totalPrice: item.price * item.quantity,
                          }}
                          onInc={() => handleIncreaseQty(item)}
                          onDec={() => handleDecreaseQty(item)}
                          onRemove={() => handleRemoveItem(item)}
                      />
                  ))
              )}
            </Stack>

            <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 1.6fr) minmax(320px, 0.9fr)",
                  },
                  gap: { xs: 3, md: 4 },
                  alignItems: "flex-start",
                }}
            >
              {/*<AddMoreSection*/}
              {/*    items={ADD_MORE_ITEMS}*/}
              {/*    onAdd={handleAddMoreItem}*/}
              {/*    onContinueShopping={handleContinueShopping}*/}
              {/*/>*/}
              <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    //mt: 1,
                  }}
              ></Box>

              <CartSummarySection
                  netValue={netValue}
                  vat={vat}
                  total={totalAfterDiscount}
                  discountAmount={discountAmount}
                  couponPercent={coupon?.percent ?? 0}
                  couponCode={coupon?.code ?? ""}
                  onApplyCoupon={handleApplyCoupon}
                  onRemoveCoupon={handleRemoveCoupon}
                  onCheckout={handleCheckout}
              />
            </Box>
          </Stack>
        </Box>
      </Box>
  );
}